import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, UserPlus, Shield, Eye, Edit3, Trash2, Mail, CheckCircle, Clock, XCircle, Settings, ArrowUpCircle, Key, Copy, RefreshCw, X
} from '../icons';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useConfirm } from '../components/Modal';
import {
  getWeddings, getCollaborators, addHelper, addDelegate,
  updateCollaborator, removeCollaborator, promoteToDelegate,
  generateHelperCode, generateDelegateCode, getInviteCodes
} from '../services/api';

function Helpers() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [confirmDialog, ConfirmDialog] = useConfirm();
  const [weddingId, setWeddingId] = useState(null);
  const [wedding, setWedding] = useState(null);
  const [helpers, setHelpers] = useState([]);
  const [delegates, setDelegates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('helpers');
  const [showAddHelper, setShowAddHelper] = useState(false);
  const [showAddDelegate, setShowAddDelegate] = useState(false);
  const [helperForm, setHelperForm] = useState({ email: '', canAddChecklist: true });
  const [delegateForm, setDelegateForm] = useState({ email: '', accessLevel: 'read' });
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [helperCode, setHelperCode] = useState(null);
  const [delegateCode, setDelegateCode] = useState(null);
  const [codeCopied, setCodeCopied] = useState(null); // 'helper' or 'delegate'
  const [loadError, setLoadError] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Only couples can manage the wedding team
    if (user.role !== 'couple') { navigate('/dashboard'); return; }

    getWeddings()
      .then((res) => {
        if (res.data.length > 0) {
          const w = res.data[0];
          setWeddingId(w.id);
          setWedding(w);
          loadCollaborators(w.id);
          // Load invite codes
          getInviteCodes(w.id).then(r => {
            setHelperCode(r.data.helper_invite_code);
            setDelegateCode(r.data.delegate_invite_code);
          }).catch(() => {});
        }
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
    // loadCollaborators only needs t for its error text; re-running on language change would refetch
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, navigate, authLoading]);

  const loadCollaborators = async (wId) => {
    try {
      const res = await getCollaborators(wId);
      setHelpers(res.data.helpers);
      setDelegates(res.data.delegates);
    } catch (err) {
      // Otherwise a failed load reads as "No helpers yet"
      setMessage({ type: 'error', text: t('coupleTools.loadFailed') });
    }
  };

  // The server still creates the invite when the email fails (emailSent: false),
  // so say so and point at the invite codes instead of claiming it was sent.
  const inviteResultMessage = (res, email) => (res.data?.emailSent === false
    ? { type: 'warning', text: t('helpers.inviteEmailFailed').replace('{email}', email) }
    : { type: 'success', text: t('helpers.inviteSent').replace('{email}', email) });

  const handleAddHelper = async (e) => {
    e.preventDefault();
    try {
      const res = await addHelper(weddingId, helperForm);
      setMessage(inviteResultMessage(res, helperForm.email));
      setHelperForm({ email: '', canAddChecklist: true });
      setShowAddHelper(false);
      await loadCollaborators(weddingId);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add helper' });
    }
  };

  const handleAddDelegate = async (e) => {
    e.preventDefault();
    try {
      const res = await addDelegate(weddingId, delegateForm);
      setMessage(inviteResultMessage(res, delegateForm.email));
      setDelegateForm({ email: '', accessLevel: 'read' });
      setShowAddDelegate(false);
      await loadCollaborators(weddingId);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to add delegate' });
    }
  };

  const handleUpdatePermissions = async (id, updates) => {
    try {
      await updateCollaborator(id, updates);
      await loadCollaborators(weddingId);
      setEditingId(null);
      setMessage({ type: 'success', text: 'Permissions updated!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to update permissions' });
    }
  };

  const handleRemove = async (id, email) => {
    const ok = await confirmDialog({ title: 'Remove Team Member', message: `Remove ${email} from your wedding team?`, danger: true, confirmText: 'Remove' });
    if (!ok) return;
    try {
      await removeCollaborator(id);
      await loadCollaborators(weddingId);
      setMessage({ type: 'success', text: `${email} has been removed` });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to remove collaborator' });
    }
  };

  const handlePromote = async (id, email) => {
    // The promote call grants read-only delegate access; "full access" overstated it
    const ok = await confirmDialog({ title: t('helpers.promoteToDelegate'), message: t('helpers.promoteConfirm').replace('{email}', email), confirmText: t('helpers.promoteToDelegate') });
    if (!ok) return;
    try {
      await promoteToDelegate(id, { accessLevel: 'read' });
      await loadCollaborators(weddingId);
      setMessage({ type: 'success', text: `${email} has been promoted to delegate!` });
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Failed to promote' });
    }
  };

  const handleGenerateHelperCode = async () => {
    try {
      const res = await generateHelperCode(weddingId);
      setHelperCode(res.data.helper_invite_code);
      setMessage({ type: 'success', text: 'New helper code generated!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to generate helper code' });
    }
  };

  const handleGenerateDelegateCode = async () => {
    try {
      const res = await generateDelegateCode(weddingId);
      setDelegateCode(res.data.delegate_invite_code);
      setMessage({ type: 'success', text: 'New delegate code generated!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to generate delegate code' });
    }
  };

  const handleCopyCode = async (code, type) => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCodeCopied(type);
      setTimeout(() => setCodeCopied(null), 2000);
    } catch {
      // Clipboard can be unavailable (some WebViews); the code is on screen to copy by hand
      setMessage({ type: 'error', text: t('helpers.copyFailed') });
    }
  };

  const statusIcon = (status) => {
    switch (status) {
      case 'accepted': return <CheckCircle size={14} className="text-green" />;
      case 'pending': return <Clock size={14} className="text-orange" />;
      case 'declined': return <XCircle size={14} className="text-red" />;
      default: return <Clock size={14} />;
    }
  };

  // Raw "pending" left couples guessing whether the invite was ever accepted
  const statusLabel = (status) => ({
    pending: t('helpers.statusPending'), accepted: t('helpers.statusAccepted'), declined: t('helpers.statusDeclined'),
  })[status] || status;

  if (loading) return <div className="loading-page"><div className="spinner"></div></div>;

  if (!wedding) {
    return (
      <div className="helpers-page">
        <div className="container">
          <div className="empty-state-sm">
            <Users size={48} />
            <h2>{loadError ? t('coupleTools.loadFailed') : t('coupleTools.noWeddingTitle')}</h2>
            {!loadError && <p>{t('helpers.noWeddingDesc')}</p>}
            {loadError ? (
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => window.location.reload()}>{t('coupleTools.tryAgain')}</button>
            ) : (
              <button className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => navigate('/dashboard')}>{t('coupleTools.goToDashboard')}</button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="helpers-page">
      <div className="container">
        {/* Header */}
        <div className="page-header">
          <div>
            <h1><Users size={28} /> {t('helpers.title')}</h1>
            <p>{t('helpers.subtitle')}</p>
          </div>
        </div>

        {/* Messages */}
        {message && (
          <div className={`alert alert-${message.type}`}>
            {message.text}
            <button className="alert-close" onClick={() => setMessage(null)}>&times;</button>
          </div>
        )}

        {/* Invite Codes Section */}
        <div className="detail-card" style={{ marginBottom: '24px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 12px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Key size={20} /> {t('helpers.inviteCodes')}
          </h3>
          <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '14px' }}>
            {t('helpers.inviteCodesDesc')}
          </p>

          {/* Helper Code */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', marginBottom: '14px', padding: '12px', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #bbf7d0' }}>
            <div>
              <strong style={{ color: '#166534' }}><UserPlus size={16} /> {t('helpers.helperCode')}</strong>
              <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '13px' }}>{t('helpers.helperCodeDesc')}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {helperCode ? (
                <>
                  <span style={{ fontFamily: 'monospace', fontSize: '20px', fontWeight: 700, letterSpacing: '3px', color: '#166534', background: '#dcfce7', padding: '6px 14px', borderRadius: '8px', border: '2px dashed #86efac' }}>
                    {helperCode}
                  </span>
                  <button className="btn btn-outline" onClick={() => handleCopyCode(helperCode, 'helper')} title="Copy code" style={{ padding: '6px 10px' }}>
                    <Copy size={16} /> {codeCopied === 'helper' ? t('common.copied') : t('common.copy')}
                  </button>
                  <button className="btn btn-outline" onClick={handleGenerateHelperCode} title="Regenerate" style={{ padding: '6px 10px' }}>
                    <RefreshCw size={16} />
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={handleGenerateHelperCode} style={{ fontSize: '13px' }}>
                  <Key size={14} /> {t('helpers.generate')}
                </button>
              )}
            </div>
          </div>

          {/* Delegate Code */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px', padding: '12px', background: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe' }}>
            <div>
              <strong style={{ color: '#9d174d' }}><Shield size={16} /> {t('helpers.delegateCode')}</strong>
              <p style={{ margin: '2px 0 0', color: '#6b7280', fontSize: '13px' }}>{t('helpers.delegateCodeDesc')}</p>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              {delegateCode ? (
                <>
                  <span style={{ fontFamily: 'monospace', fontSize: '20px', fontWeight: 700, letterSpacing: '3px', color: '#1e40af', background: '#dbeafe', padding: '6px 14px', borderRadius: '8px', border: '2px dashed #93c5fd' }}>
                    {delegateCode}
                  </span>
                  <button className="btn btn-outline" onClick={() => handleCopyCode(delegateCode, 'delegate')} title="Copy code" style={{ padding: '6px 10px' }}>
                    <Copy size={16} /> {codeCopied === 'delegate' ? t('common.copied') : t('common.copy')}
                  </button>
                  <button className="btn btn-outline" onClick={handleGenerateDelegateCode} title="Regenerate" style={{ padding: '6px 10px' }}>
                    <RefreshCw size={16} />
                  </button>
                </>
              ) : (
                <button className="btn btn-primary" onClick={handleGenerateDelegateCode} style={{ fontSize: '13px' }}>
                  <Key size={14} /> {t('helpers.generate')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="category-tabs">
          <button className={activeTab === 'helpers' ? 'active' : ''} onClick={() => setActiveTab('helpers')}>
            <UserPlus size={16} /> {t('helpers.helpersTab')} ({helpers.length})
          </button>
          <button className={activeTab === 'delegates' ? 'active' : ''} onClick={() => setActiveTab('delegates')}>
            <Shield size={16} /> {t('helpers.delegatesTab')} ({delegates.length})
          </button>
        </div>

        {/* Helpers Tab */}
        {activeTab === 'helpers' && (
          <div className="collaborators-section">
            <div className="section-header">
              <div>
                <h2>{t('helpers.helpersTab')}</h2>
                <p className="section-desc">
                  {t('helpers.helpersDesc')}
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowAddHelper(!showAddHelper)}>
                <UserPlus size={16} /> {t('helpers.addHelper')}
              </button>
            </div>

            {/* Add Helper Form */}
            {showAddHelper && (
              <form className="add-form collab-form" onSubmit={handleAddHelper}>
                <h3>{t('helpers.inviteHelper')}</h3>
                <div className="form-group">
                  <label>{t('helpers.emailAddress')}</label>
                  <input
                    type="email"
                    placeholder={t('helpers.emailPlaceholder')}
                    value={helperForm.email}
                    onChange={(e) => setHelperForm({ ...helperForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={helperForm.canAddChecklist}
                      onChange={(e) => setHelperForm({ ...helperForm, canAddChecklist: e.target.checked })}
                    />
                    {t('helpers.allowAddTasks')}
                  </label>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary"><Mail size={16} /> {t('helpers.sendInvitation')}</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowAddHelper(false)}>{t('common.cancel')}</button>
                </div>
              </form>
            )}

            {/* Helpers List */}
            {helpers.length === 0 ? (
              <div className="empty-state-sm">
                <UserPlus size={32} />
                <p>{t('helpers.noHelpers')}</p>
              </div>
            ) : (
              <div className="collaborator-list">
                {helpers.map((h) => (
                  <div key={h.id} className={`collaborator-card ${h.invite_status}`}>
                    <div className="collab-avatar">
                      {h.first_name ? `${h.first_name[0]}${h.last_name?.[0] || ''}` : h.invited_email[0].toUpperCase()}
                    </div>
                    <div className="collab-info">
                      <div className="collab-name">
                        {h.first_name ? `${h.first_name} ${h.last_name}` : h.invited_email}
                        <span className={`status-badge ${h.invite_status}`}>
                          {statusIcon(h.invite_status)} {statusLabel(h.invite_status)}
                        </span>
                      </div>
                      <div className="collab-email">{h.invited_email}</div>
                      <div className="collab-permissions">
                        {h.can_view_checklist && <span className="perm-tag">{t('helpers.viewChecklist')}</span>}
                        {h.can_assign_checklist && <span className="perm-tag">{t('helpers.assignTasks')}</span>}
                        {h.can_add_checklist && <span className="perm-tag">{t('helpers.addTasks')}</span>}
                      </div>
                    </div>
                    <div className="collab-actions">
                      {editingId === h.id ? (
                        <div className="edit-perms">
                          <label>
                            <input type="checkbox" defaultChecked={h.can_add_checklist}
                              onChange={(e) => handleUpdatePermissions(h.id, { canAddChecklist: e.target.checked })}
                            /> {t('helpers.canAddTasks')}
                          </label>
                          <button className="icon-btn" title={t('common.cancel')} onClick={() => setEditingId(null)} style={{ marginLeft: '8px' }}>
                            <XCircle size={16} /> {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <button className="icon-btn" title={t('helpers.promoteToDelegate')} onClick={() => handlePromote(h.id, h.invited_email)}>
                            <ArrowUpCircle size={16} />
                          </button>
                          <button className="icon-btn" title={t('helpers.editPermissions')} onClick={() => setEditingId(h.id)}>
                            <Settings size={16} />
                          </button>
                          <button className="icon-btn danger" title={t('common.remove')} onClick={() => handleRemove(h.id, h.invited_email)}>
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Delegates Tab */}
        {activeTab === 'delegates' && (
          <div className="collaborators-section">
            <div className="section-header">
              <div>
                <h2>{t('helpers.delegatesTab')}</h2>
                <p className="section-desc">
                  {t('helpers.delegatesDesc')}
                </p>
              </div>
              <button className="btn btn-primary" onClick={() => setShowAddDelegate(!showAddDelegate)}>
                <Shield size={16} /> {t('helpers.addDelegate')}
              </button>
            </div>

            {/* Add Delegate Form */}
            {showAddDelegate && (
              <form className="add-form collab-form" onSubmit={handleAddDelegate}>
                <h3>{t('helpers.inviteDelegate')}</h3>
                <div className="form-group">
                  <label>{t('helpers.emailAddress')}</label>
                  <input
                    type="email"
                    placeholder={t('helpers.delegateEmailPlaceholder')}
                    value={delegateForm.email}
                    onChange={(e) => setDelegateForm({ ...delegateForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>{t('helpers.accessLevel')}</label>
                  <select
                    value={delegateForm.accessLevel}
                    onChange={(e) => setDelegateForm({ ...delegateForm, accessLevel: e.target.value })}
                  >
                    <option value="read">{t('helpers.readOnly')}</option>
                    <option value="write">{t('helpers.readWrite')}</option>
                  </select>
                </div>
                <div className="form-actions">
                  <button type="submit" className="btn btn-primary"><Mail size={16} /> {t('helpers.sendInvitation')}</button>
                  <button type="button" className="btn btn-outline" onClick={() => setShowAddDelegate(false)}>{t('common.cancel')}</button>
                </div>
              </form>
            )}

            {/* Delegates List */}
            {delegates.length === 0 ? (
              <div className="empty-state-sm">
                <Shield size={32} />
                <p>{t('helpers.noDelegates')}</p>
              </div>
            ) : (
              <div className="collaborator-list">
                {delegates.map((d) => (
                  <div key={d.id} className={`collaborator-card ${d.invite_status}`}>
                    <div className="collab-avatar">
                      {d.first_name ? `${d.first_name[0]}${d.last_name?.[0] || ''}` : d.invited_email[0].toUpperCase()}
                    </div>
                    <div className="collab-info">
                      <div className="collab-name">
                        {d.first_name ? `${d.first_name} ${d.last_name}` : d.invited_email}
                        <span className={`status-badge ${d.invite_status}`}>
                          {statusIcon(d.invite_status)} {statusLabel(d.invite_status)}
                        </span>
                      </div>
                      <div className="collab-email">{d.invited_email}</div>
                      <div className="collab-permissions">
                        <span className={`perm-tag ${d.access_level === 'write' ? 'write' : 'read'}`}>
                          {d.access_level === 'write' ? <><Edit3 size={12} /> {t('helpers.readWriteBadge')}</> : <><Eye size={12} /> {t('helpers.readOnlyBadge')}</>}
                        </span>
                      </div>
                    </div>
                    <div className="collab-actions">
                      {editingId === d.id ? (
                        <div className="edit-perms">
                          <select defaultValue={d.access_level}
                            onChange={(e) => handleUpdatePermissions(d.id, { accessLevel: e.target.value })}
                          >
                            <option value="read">{t('helpers.readOnlyBadge')}</option>
                            <option value="write">{t('helpers.readWriteBadge')}</option>
                          </select>
                          <button className="icon-btn" title={t('common.cancel')} onClick={() => setEditingId(null)} style={{ marginLeft: '8px' }}>
                            <XCircle size={16} /> {t('common.cancel')}
                          </button>
                        </div>
                      ) : (
                        <>
                          <button className="icon-btn" title={t('helpers.editPermissions')} onClick={() => setEditingId(d.id)}>
                            <Settings size={16} />
                          </button>
                          <button className="icon-btn danger" title={t('common.remove')} onClick={() => handleRemove(d.id, d.invited_email)}>
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {ConfirmDialog}
    </div>
  );
}

export default Helpers;
